"use client";

import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { passwordInputClass } from "@/lib/auth/password";

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  invalid?: boolean;
  error?: string | null;
  errorId?: string;
}

/**
 * @component PasswordInput
 * @description Controlled password input with Eye/EyeOff show-hide toggle.
 * Applies `passwordInputClass` from lib/auth/password and optionally renders a
 * label above and an error message below the field.
 * @param id - Input element id (also used as label's htmlFor).
 * @param value - Controlled input value.
 * @param onChange - Change handler.
 * @param label - Optional label text rendered above the input.
 * @param placeholder - Input placeholder.
 * @param required - Whether the field is required.
 * @param autoComplete - HTML autocomplete attribute value.
 * @param invalid - Marks the input as invalid (adds destructive border).
 * @param error - Error message rendered below the input.
 * @param errorId - Id for the error element (used with aria-describedby).
 * @returns A password input with show/hide toggle.
 */
export default function PasswordInput({
  id,
  value,
  onChange,
  label,
  placeholder,
  required,
  autoComplete,
  invalid,
  error,
  errorId,
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const isInvalid = invalid || !!error;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-label text-muted-text">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={isInvalid || undefined}
          aria-describedby={errorId}
          className={`${passwordInputClass}${isInvalid ? " border-destructive focus:border-destructive" : ""}`}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-text hover:text-charcoal transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          {showPassword
            ? <EyeOff className="w-5 h-5" aria-hidden />
            : <Eye className="w-5 h-5" aria-hidden />}
        </button>
      </div>
      {error && (
        <p id={errorId} className="text-body-base text-destructive mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
